import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Loader2, Plus, SlidersHorizontal, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLURAL = { contact: 'contacts', company: 'companies' };

// HubSpot-imported enumeration properties may store options as objects
// ({label, value}) rather than plain strings. Normalise so we never render
// an object as a React child (which throws a runtime error).
const optValue = (o) => (o && typeof o === 'object') ? String(o.value ?? o.label ?? '') : String(o ?? '');
const optLabel = (o) => (o && typeof o === 'object') ? String(o.label ?? o.value ?? '') : String(o ?? '');
const primitive = (v) => (v && typeof v === 'object') ? (v.value ?? v.label ?? '') : v;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Multi-line text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown (single)' },
  { value: 'multiselect', label: 'Dropdown (multi)' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'url', label: 'URL' },
];

const CustomFields = ({ objectType, recordId, value, onSaved }) => {
  const [props, setProps] = useState([]);
  const [vals, setVals] = useState(value || {});
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newField, setNewField] = useState({ label: '', field_type: 'text', options: '' });
  const [creating, setCreating] = useState(false);

  const fetchProps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/properties`, { params: { object_type: objectType } });
      setProps(res.data.properties || []);
    } catch { /* ignore */ }
  }, [objectType]);

  useEffect(() => { fetchProps(); }, [fetchProps]);
  useEffect(() => { setVals(value || {}); }, [value]);

  const setVal = (name, v) => setVals(prev => ({ ...prev, [name]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/${PLURAL[objectType]}/${recordId}`, { custom_fields: vals });
      toast.success('Properties saved');
      if (onSaved) onSaved();
    } catch { toast.error('Failed to save properties'); }
    finally { setSaving(false); }
  };

  const createField = async (e) => {
    e.preventDefault();
    if (!newField.label.trim()) return;
    setCreating(true);
    try {
      const payload = {
        object_type: objectType,
        label: newField.label.trim(),
        field_type: newField.field_type,
      };
      if (['select', 'multiselect'].includes(newField.field_type)) {
        payload.options = newField.options.split(',').map(s => s.trim()).filter(Boolean);
        if (payload.options.length === 0) { toast.error('Add at least one option'); setCreating(false); return; }
      }
      await axios.post(`${API}/properties`, payload);
      toast.success('Field created');
      setNewField({ label: '', field_type: 'text', options: '' });
      setShowAdd(false);
      fetchProps();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create field'); }
    finally { setCreating(false); }
  };

  const renderInput = (p) => {
    const v = primitive(vals[p.name]) ?? '';
    const cls = 'bg-slate-900 border-slate-800 h-9';
    switch (p.field_type) {
      case 'textarea':
        return <textarea value={v} onChange={e => setVal(p.name, e.target.value)} rows={2} className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-200" data-testid={`cf-${p.name}`} />;
      case 'number':
        return <Input type="number" value={v} onChange={e => setVal(p.name, e.target.value)} className={cls} data-testid={`cf-${p.name}`} />;
      case 'date':
        return <Input type="date" value={v} onChange={e => setVal(p.name, e.target.value)} className={cls} data-testid={`cf-${p.name}`} />;
      case 'url':
        return <Input type="url" value={v} onChange={e => setVal(p.name, e.target.value)} className={cls} data-testid={`cf-${p.name}`} />;
      case 'checkbox':
        return (
          <button type="button" onClick={() => setVal(p.name, !v)} className={`w-9 h-9 rounded-md border flex items-center justify-center ${v ? 'bg-violet-600 border-violet-500' : 'bg-slate-900 border-slate-800'}`} data-testid={`cf-${p.name}`}>
            {v ? <Check className="w-4 h-4 text-white" /> : null}
          </button>
        );
      case 'select':
        return (
          <Select value={v || 'none'} onValueChange={x => setVal(p.name, x === 'none' ? '' : x)}>
            <SelectTrigger className={cls} data-testid={`cf-${p.name}`}><SelectValue placeholder="-" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="none">-</SelectItem>
              {(p.options || []).map(o => {
                const ov = optValue(o), ol = optLabel(o);
                return <SelectItem key={ov} value={ov}>{ol}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        );
      case 'multiselect': {
        const arr = Array.isArray(v) ? v : (v ? [v] : []);
        return (
          <div className="flex flex-wrap gap-1.5" data-testid={`cf-${p.name}`}>
            {(p.options || []).map(o => {
              const ov = optValue(o), ol = optLabel(o);
              const on = arr.includes(ov);
              return (
                <button key={ov} type="button" onClick={() => setVal(p.name, on ? arr.filter(x => x !== ov) : [...arr, ov])}
                  className={`text-xs px-2 py-1 rounded-full border ${on ? 'bg-violet-600 border-violet-500 text-white' : 'bg-slate-900 border-slate-800 text-slate-400'}`}>
                  {ol}
                </button>
              );
            })}
          </div>
        );
      }
      default:
        return <Input value={v} onChange={e => setVal(p.name, e.target.value)} className={cls} data-testid={`cf-${p.name}`} />;
    }
  };

  return (
    <div className="border-t border-slate-800 pt-4" data-testid="custom-fields">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-slate-300 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Custom Properties</Label>
        <Button size="sm" variant="ghost" className="text-violet-400 h-7" onClick={() => setShowAdd(s => !s)} data-testid="toggle-add-field">
          {showAdd ? <X className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />} {showAdd ? 'Cancel' : 'New field'}
        </Button>
      </div>

      {showAdd && (
        <form onSubmit={createField} className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-2 mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input value={newField.label} onChange={e => setNewField({ ...newField, label: e.target.value })} placeholder="Field name" className="bg-slate-900 border-slate-800 h-9" data-testid="new-field-label" />
            <Select value={newField.field_type} onValueChange={v => setNewField({ ...newField, field_type: v })}>
              <SelectTrigger className="bg-slate-900 border-slate-800 h-9"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {['select', 'multiselect'].includes(newField.field_type) && (
            <Input value={newField.options} onChange={e => setNewField({ ...newField, options: e.target.value })} placeholder="Options (comma-separated)" className="bg-slate-900 border-slate-800 h-9" data-testid="new-field-options" />
          )}
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={creating} className="bg-violet-600 hover:bg-violet-500" data-testid="create-field-btn">
              {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create field'}
            </Button>
          </div>
        </form>
      )}

      {props.length === 0 ? (
        <p className="text-xs text-slate-600 italic">No custom properties yet. Click "New field" to add one.</p>
      ) : (
        <div className="space-y-3">
          {props.map(p => (
            <div key={p.id}>
              <Label className="text-slate-400 text-xs mb-1 block">{p.label}</Label>
              {renderInput(p)}
            </div>
          ))}
          <div className="flex justify-end">
            <Button size="sm" onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-500" data-testid="save-custom-fields">
              {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving</> : 'Save properties'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFields;
